import { useEffect, useMemo, useState } from "react";
import { MAX_PAGE_SIZE } from "@/constants/pagination";

import { useModules } from "@/api/features/modules/queries";
import type { Module, PaginatedModules } from "@/api/features/modules/types";
import { useUserModules } from "@/api/features/users/queries";
import {
  useGrantUserModulesMutation,
  useRevokeUserModulesMutation,
} from "@/api/features/users/mutations";

function extractErrorMessage(err: unknown): string | null {
  const anyErr = err as any;
  const payload = anyErr?.response?.data ?? anyErr?.payload ?? anyErr?.data;
  return payload?.message ?? anyErr?.message ?? null;
}

export default function ModuleAccessCard({
  userId,
  entityLabel,
}: {
  userId: number;
  entityLabel: string;
}) {
// The API caps `limit` at 100 and REJECTS anything larger with a 422 — the
// validation middleware safeParses and returns, it does not clamp. So a limit
// over the cap fetches nothing at all, and no limit at all falls back to the
// server default of 10. Either way a module can exist, be grantable in the
// database, and simply never appear on screen to be ticked.
  const modulesQuery = useModules({ page: 1, limit: MAX_PAGE_SIZE });
  const userModulesQuery = useUserModules(userId);
  const grantMutation = useGrantUserModulesMutation();
  const revokeMutation = useRevokeUserModulesMutation();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const modules = useMemo(() => {
    const data = modulesQuery.data;
    if (!data) return [] as Module[];
    if (Array.isArray(data)) return data as Module[];
    return ((data as PaginatedModules).items ?? []) as Module[];
  }, [modulesQuery.data]);

  const assignedIds = useMemo(
    () => (userModulesQuery.data ?? []).map((module) => Number(module.id)).filter(Boolean),
    [userModulesQuery.data]
  );

  useEffect(() => {
    setSelectedIds(assignedIds);
  }, [assignedIds]);

  const isDirty = useMemo(() => {
    const current = new Set(assignedIds);
    const next = new Set(selectedIds);
    if (current.size !== next.size) return true;
    for (const id of current) {
      if (!next.has(id)) return true;
    }
    return false;
  }, [assignedIds, selectedIds]);

  const saving = grantMutation.isPending || revokeMutation.isPending;
  const errorMessage =
    extractErrorMessage(grantMutation.error) ||
    extractErrorMessage(revokeMutation.error);

  const toggle = (moduleId: number) => {
    setStatusMessage(null);
    setSelectedIds((prev) =>
      prev.includes(moduleId)
        ? prev.filter((id) => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSave = async () => {
    const current = new Set(assignedIds);
    const next = new Set(selectedIds);
    const toGrant = selectedIds.filter((id) => !current.has(id));
    const toRevoke = assignedIds.filter((id) => !next.has(id));

    setStatusMessage(null);

    try {
      if (toGrant.length) {
        await grantMutation.mutateAsync({ userId, moduleIds: toGrant });
      }
      if (toRevoke.length) {
        await revokeMutation.mutateAsync({ userId, moduleIds: toRevoke });
      }
      if (!toGrant.length && !toRevoke.length) {
        setStatusMessage("No module changes to save.");
        return;
      }
      setStatusMessage("Module access updated.");
    } catch {
      // mutation state surfaces the error
    }
  };

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Module Access</h2>
          <p className="text-sm text-gray-600">
            Select which modules this {entityLabel.toLowerCase()} login can access.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving || modulesQuery.isLoading || userModulesQuery.isLoading}
          className="inline-flex items-center justify-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save Module Access"}
        </button>
      </div>

      {errorMessage ? (
        <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      {statusMessage ? (
        <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {statusMessage}
        </div>
      ) : null}

      {modulesQuery.isLoading || userModulesQuery.isLoading ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600">
          Loading modules...
        </div>
      ) : modules.length === 0 ? (
        <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-6 text-sm text-amber-700">
          No modules found. Create modules first, then assign them here.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((module) => {
            const checked = selectedIds.includes(module.id);
            return (
              <label
                key={module.id}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 text-sm transition ${
                  checked
                    ? "border-gray-900 bg-gray-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(module.id)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                />
                <span className="min-w-0">
                  <span className="block font-medium text-gray-900">{module.name}</span>
                  {module.parent?.name ? (
                    <span className="block text-xs text-gray-500">
                      Under {module.parent.name}
                    </span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </section>
  );
}
